import React from 'react';
import { Dropdown, Modal, Spin, message } from 'antd';
import { DownOutlined, FileOutlined, LinkOutlined, PictureOutlined, SwapOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useProjectChangeRequests } from '@hooks/projects/useProjectChangeRequests';
import StatusBadge from '@/components/ui/StatusBadge';
import Markdown from '@/components/ui/Markdown';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import AntImagePreview from '@/components/ui/AntImagePreview';
import {
    getAttachmentFileExtension,
    isLikelyImageAttachmentUrl,
    normalizePhaseLinkUrl,
    resolveAttachmentUrl,
    truncateFileName,
} from '@hooks/projects/projectHelpers';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    MutedText,
    PanelDivider,
    CardWithMinHeight,
    ContentCardTitle,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
    ThreeTwoOneCardGridScroll,
} from '../style';

export default function ProjectChangeRequestsTab({ projectId }) {
    const {
        isClientView,
        canView,
        canResolve,
        changeRequests,
        total,
        isLoading,
        updateChangeRequestPending,
        pendingUpdateRequestId,
        resolveChangeRequest,
    } = useProjectChangeRequests(projectId);

    const [previewUrl, setPreviewUrl] = React.useState(null);

    const listItems = Array.isArray(changeRequests) ? changeRequests : [];

    if (!canView) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <SwapOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>You don&apos;t have permission to view change requests</EmptyStateTitle>
                <EmptyStateDescription>Contact your admin if you need access.</EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    return (
        <>
            <Container
                $display="flex"
                $flex="1"
                $minHeight="0"
                $overflow="hidden"
                $backgroundColor="var(--ant-color-fill-quaternary, '#FAFAFA')"
            >
                <BoardTabRoot
                    style={{
                        flex: 1,
                        minWidth: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <BoardTabRoot
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            '--board-kanban-inline-gutter': 'clamp(16px, 4vw, 40px)',
                        }}
                    >
                        <Container
                            $display="flex"
                            $justifyContent="space-between"
                            $alignItems="flex-start"
                            $gap="16px"
                            $flexWrap="wrap"
                            $marginBottom="16px"
                        >
                            <Container>
                                <BoardPageTitle>Change Requests</BoardPageTitle>
                                <BoardPageSubtitle>
                                    Track client-requested scope changes with notes, links, and attachments.
                                </BoardPageSubtitle>
                            </Container>
                            <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap">
                                <MutedText style={{ margin: 0, fontSize: 13 }}>
                                    {total} change request{total !== 1 ? 's' : ''}
                                </MutedText>
                            </Container>
                        </Container>

                        {isLoading ? (
                            <EmptyStateBlock>
                                <DetailPanelLoading>
                                    <Spin size="large" />
                                </DetailPanelLoading>
                            </EmptyStateBlock>
                        ) : listItems.length === 0 ? (
                            <EmptyStateBlock>
                                <EmptyStateIcon>
                                    <SwapOutlined />
                                </EmptyStateIcon>
                                <EmptyStateTitle>No change requests yet</EmptyStateTitle>
                                <EmptyStateDescription>
                                    Change requests appear here when a client requests updates, adjustments, or new scope.
                                </EmptyStateDescription>
                            </EmptyStateBlock>
                        ) : (
                            <ThreeTwoOneCardGridScroll style={{ padding: 12 }}>
                                {listItems.map((item) => {
                                    const status = item?.status ?? null;
                                    const number = item?.changeRequestNumber ?? item?.number ?? null;
                                    const rawNotes =
                                        item?.notes?.markdown ??
                                        item?.content ??
                                        item?.notesHtml ??
                                        item?.notes?.html ??
                                        item?.notes?.content ??
                                        (typeof item?.notes === 'string' ? item.notes : '');
                                    const notesMarkdown = (() => {
                                        const s = String(rawNotes ?? '').trim();
                                        if (!s) return '';
                                        const looksHtml = /<[^>]+>/.test(s) || s.includes('&nbsp;');
                                        return looksHtml ? htmlToMarkdown(s).trim() : s;
                                    })();
                                    const links = Array.isArray(item?.links) ? item.links : [];
                                    const attachments = Array.isArray(item?.attachments) ? item.attachments : [];

                                    const showNotes = Boolean(notesMarkdown);
                                    const showLinks = Array.isArray(links) && links.length > 0;
                                    const showAttachments = Array.isArray(attachments) && attachments.length > 0;
                                    const hasResources = showLinks || showAttachments;
                                    const canShowResolve = !isClientView;
                                    const isResolved = String(status ?? '').toLowerCase() === 'approved';
                                    const requestId = item?.id ?? null;
                                    const isResolvingThis =
                                        Boolean(requestId) && updateChangeRequestPending && pendingUpdateRequestId === requestId;
                                    const attachmentItems = [...links, ...attachments];

                                    const handleNotesLinkClick = (e, href, label) => {
                                        e?.preventDefault?.();
                                        const h = String(href || '');
                                        if (!h) return;

                                        const attMatch = /^#attachment-(\d+)$/.exec(h);
                                        if (attMatch) {
                                            const idx = parseInt(attMatch[1], 10);
                                            const filesOnly = attachmentItems.filter(
                                                (x) => String(x?.type || 'file').toLowerCase() !== 'link',
                                            );
                                            const it = filesOnly[idx] ?? attachmentItems[idx];
                                            const resolved = it?.url ? resolveAttachmentUrl(it.url) : '';
                                            const title = it?.title || it?.name || label || 'Attachment';
                                            if (resolved && isLikelyImageAttachmentUrl(resolved)) {
                                                setPreviewUrl(resolved);
                                                return;
                                            }
                                            if (resolved) window.open(resolved, '_blank', 'noopener,noreferrer');
                                            return;
                                        }

                                        const normalized = normalizePhaseLinkUrl(h) || h;
                                        if (normalized && isLikelyImageAttachmentUrl(normalized)) {
                                            setPreviewUrl(normalized);
                                            return;
                                        }
                                        if (normalized) window.open(normalized, '_blank', 'noopener,noreferrer');
                                    };

                                    return (
                                        <CardWithMinHeight
                                            key={item?.id ?? item?._id ?? number ?? String(item?.createdAt ?? '')}
                                            $padding="16px"
                                            $minHeight="300px"
                                            $noHoverShadow
                                            style={{
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            justifyContent: 'space-between',
                                                            gap: 8,
                                                            marginBottom: 8,
                                                        }}
                                                    >
                                                        <ContentCardTitle $marginBottom="0" style={{ flex: 1, minWidth: 0 }}>
                                                            {number ? `${number}` : 'Change request'}
                                                        </ContentCardTitle>
                                                        {status != null && <StatusBadge status={status} size="compact" />}
                                                    </div>

                                                    <PanelDivider />

                                                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingRight: 4 }}>
                                                        <div
                                                            style={{
                                                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                borderRadius: 10,
                                                                minHeight: 150,
                                                                padding: '10px 12px',
                                                                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    letterSpacing: '0.04em',
                                                                    textTransform: 'uppercase',
                                                                    color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                    marginBottom: 6,
                                                                    fontFamily: 'var(--font-family-geist)',
                                                                }}
                                                            >
                                                                Notes
                                                            </div>
                                                            {showNotes ? (
                                                                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                                                    <Markdown
                                                                        source={notesMarkdown}
                                                                        variant="compact"
                                                                        lineClamp={6}
                                                                        linkPrimary
                                                                        attachmentItems={attachmentItems}
                                                                        components={{
                                                                            a: ({ href, children }) => {
                                                                                const label = Array.isArray(children) ? children.map(String).join('') : String(children ?? '');
                                                                                return (
                                                                                    <a
                                                                                        href={href}
                                                                                        onClick={(e) => handleNotesLinkClick(e, href, label)}
                                                                                        style={{
                                                                                            color: 'var(--ant-color-primary, #7c3aed)',
                                                                                            textDecoration: 'underline',
                                                                                        }}
                                                                                    >
                                                                                        {children}
                                                                                    </a>
                                                                                );
                                                                            },
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>
                                                                    —
                                                                </div>
                                                            )}
                                                        </div>

                                                    </div>

                                                    {(hasResources || canShowResolve) && <PanelDivider />}

                                                    <Container
                                                        $display="flex"
                                                        $alignItems="center"
                                                        $justifyContent="space-between"
                                                        $gap="10px"
                                                        style={{ flexShrink: 0, paddingTop: 2 }}
                                                    >
                                                        {hasResources ? (
                                                            <Dropdown
                                                                trigger={['click']}
                                                                placement="bottomLeft"
                                                                dropdownRender={() => (
                                                                    <div
                                                                        style={{
                                                                            minWidth: 320,
                                                                            maxWidth: 420,
                                                                            padding: 12,
                                                                            borderRadius: 12,
                                                                            border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                            background: 'var(--ant-color-bg-container, #fff)',
                                                                            boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                                                                        }}
                                                                    >
                                                                        {showLinks && (
                                                                            <div style={{ marginBottom: showAttachments ? 12 : 0 }}>
                                                                                <MutedText
                                                                                    style={{
                                                                                        margin: '0 0 8px 0',
                                                                                        fontSize: 11,
                                                                                        fontWeight: 700,
                                                                                        letterSpacing: '0.04em',
                                                                                        textTransform: 'uppercase',
                                                                                        opacity: 0.85,
                                                                                        display: 'inline-flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 6,
                                                                                    }}
                                                                                >
                                                                                    <LinkOutlined />
                                                                                    Links
                                                                                </MutedText>
                                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                                    {links.map((l, idx) => {
                                                                                        const rawUrl = l?.url ?? '';
                                                                                        const href = normalizePhaseLinkUrl(rawUrl);
                                                                                        const label = (l?.title || rawUrl || 'Link').trim();
                                                                                        return (
                                                                                            <a
                                                                                                key={`${rawUrl || 'link'}-${idx}`}
                                                                                                href={href || '#'}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                style={{
                                                                                                    display: 'inline-flex',
                                                                                                    alignItems: 'center',
                                                                                                    gap: 6,
                                                                                                    maxWidth: '100%',
                                                                                                    textDecoration: 'none',
                                                                                                    color: 'var(--ant-color-primary, #7c3aed)',
                                                                                                    border: '1px solid rgba(124,58,237,0.22)',
                                                                                                    background: 'var(--ant-color-primary-bg, rgba(124,58,237,0.08))',
                                                                                                    borderRadius: 999,
                                                                                                    padding: '6px 10px',
                                                                                                    fontSize: 13,
                                                                                                    fontWeight: 400,
                                                                                                    overflowWrap: 'anywhere',
                                                                                                    wordBreak: 'break-word',
                                                                                                }}
                                                                                            >
                                                                                                <LinkOutlined style={{ fontSize: 12, flexShrink: 0 }} />
                                                                                                <span style={{ minWidth: 0 }}>{label}</span>
                                                                                            </a>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {showAttachments && (
                                                                            <div>
                                                                                <MutedText
                                                                                    style={{
                                                                                        margin: '0 0 8px 0',
                                                                                        fontSize: 11,
                                                                                        fontWeight: 700,
                                                                                        letterSpacing: '0.04em',
                                                                                        textTransform: 'uppercase',
                                                                                        opacity: 0.85,
                                                                                        display: 'inline-flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 6,
                                                                                    }}
                                                                                >
                                                                                    <FileOutlined />
                                                                                    Attachments
                                                                                </MutedText>
                                                                                <div
                                                                                    style={{
                                                                                        display: 'grid',
                                                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                                                                        gap: 10,
                                                                                        width: '100%',
                                                                                    }}
                                                                                >
                                                                                    {attachments.map((att, idx) => {
                                                                                        const rawUrl = att?.url ?? att?.rawUrl ?? '';
                                                                                        const href = resolveAttachmentUrl(rawUrl);
                                                                                        const isImage = isLikelyImageAttachmentUrl(rawUrl);
                                                                                        const ext = getAttachmentFileExtension(rawUrl);
                                                                                        const title = att?.title || att?.name || 'Attachment';
                                                                                        return (
                                                                                            <a
                                                                                                key={`${rawUrl || 'attachment'}-${idx}`}
                                                                                                href={href || '#'}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                style={{
                                                                                                    display: 'flex',
                                                                                                    flexDirection: 'column',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center',
                                                                                                    minHeight: 110,
                                                                                                    borderRadius: 10,
                                                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                                                    background:
                                                                                                        'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                                                    padding: 8,
                                                                                                    textDecoration: 'none',
                                                                                                    color: 'inherit',
                                                                                                    gap: 8,
                                                                                                    overflow: 'hidden',
                                                                                                }}
                                                                                            >
                                                                                                {isImage ? (
                                                                                                    <img
                                                                                                        src={href}
                                                                                                        alt={title}
                                                                                                        style={{
                                                                                                            width: '100%',
                                                                                                            height: 66,
                                                                                                            objectFit: 'cover',
                                                                                                            borderRadius: 8,
                                                                                                        }}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <div
                                                                                                        style={{
                                                                                                            width: '100%',
                                                                                                            height: 66,
                                                                                                            borderRadius: 8,
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center',
                                                                                                            background:
                                                                                                                'var(--ant-color-bg-elevated, rgba(255,255,255,0.75))',
                                                                                                        }}
                                                                                                    >
                                                                                                        <FileOutlined style={{ fontSize: 22 }} />
                                                                                                    </div>
                                                                                                )}
                                                                                                <span
                                                                                                    style={{
                                                                                                        fontSize: 11,
                                                                                                        lineHeight: 1.3,
                                                                                                        textAlign: 'center',
                                                                                                        maxWidth: '100%',
                                                                                                        overflow: 'hidden',
                                                                                                        textOverflow: 'ellipsis',
                                                                                                        whiteSpace: 'nowrap',
                                                                                                    }}
                                                                                                    title={title}
                                                                                                >
                                                                                                    {truncateFileName(title, 18)}
                                                                                                    {!isImage && ext ? ` (${ext})` : ''}
                                                                                                </span>
                                                                                            </a>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            >
                                                                <Button
                                                                    type="button"
                                                                    $primary={false}
                                                                    $size="12px"
                                                                    style={{ width: 'fit-content' }}
                                                                >
                                                                    Attachments

                                                                    <DownOutlined style={{ fontSize: 11, marginLeft: 6 }} />
                                                                </Button>
                                                            </Dropdown>
                                                        ) : (
                                                            <span />
                                                        )}

                                                        {canShowResolve ? (
                                                            <Button
                                                                type="button"
                                                                $primary
                                                                $blackOnLight
                                                                $size="12px"
                                                                $padding="6px 12px"
                                                                loading={isResolvingThis}
                                                                disabled={isResolvingThis || isResolved}
                                                                disabledTooltip={isResolved ? 'Already resolved' : undefined}
                                                                permissionDenied={!canResolve}
                                                                onClick={() => {
                                                                    if (!requestId) return;
                                                                    Modal.confirm({
                                                                        title: 'Resolve this change request?',
                                                                        content: 'This will mark it as resolved (approved).',
                                                                        okText: 'Yes, resolve',
                                                                        cancelText: 'No',
                                                                        okButtonProps: { danger: false },
                                                                        onOk: () =>
                                                                            resolveChangeRequest(requestId, {
                                                                                onSuccess: () => message.success('Change request resolved'),
                                                                                onError: (err) =>
                                                                                    message.error(
                                                                                        err?.response?.data?.message ||
                                                                                        err?.message ||
                                                                                        'Failed to resolve change request',
                                                                                    ),
                                                                            }),
                                                                    });
                                                                }}
                                                            >
                                                                Resolve
                                                            </Button>
                                                        ) : (
                                                            <span />
                                                        )}
                                                    </Container>
                                        </CardWithMinHeight>
                                    );
                                })}
                            </ThreeTwoOneCardGridScroll>
                        )}
                    </BoardTabRoot>
                </BoardTabRoot>
            </Container>
            <AntImagePreview open={!!previewUrl} src={previewUrl} onClose={() => setPreviewUrl(null)} />
        </>
    );
}
